/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { styled } from '@superset-ui/core';
import {
  EDITOR_MODAL_DIMENSIONS,
  EDITOR_MODAL_TOKENS,
  getEditorPaneShadow,
  getEditorPanelBorder,
} from './editorTokens';

export const EditorContainer = styled.div`
  height: ${EDITOR_MODAL_DIMENSIONS.containerHeight};
  min-height: ${EDITOR_MODAL_DIMENSIONS.containerMinHeight}px;
  padding: ${({ theme }) => theme.gridUnit / 2}px 0;
  background: ${({ theme }) => theme.colors.grayscale.light5};
`;

export const EditorSplitLayout = styled.div`
  display: grid;
  grid-template-columns:
    ${({ theme }) =>
      theme.gridUnit * EDITOR_MODAL_DIMENSIONS.splitPaneWidthUnits}px
    minmax(0, 1fr);
  gap: ${({ theme }) => theme.gridUnit * 3.5}px;
  height: 100%;

  @media (max-width: ${({ theme }) =>
      theme.gridUnit * EDITOR_MODAL_DIMENSIONS.splitLayoutBreakpointUnits}px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

export const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: ${({ theme }) => getEditorPanelBorder(theme)};
  border-radius: ${({ theme }) =>
    theme.gridUnit * EDITOR_MODAL_TOKENS.paneRadiusUnits}px;
  background: ${({ theme }) => theme.colors.grayscale.light5};
  box-shadow: ${() => getEditorPaneShadow()};
  overflow: hidden;
`;

export const EditorPaneHeader = styled.div`
  padding: ${({ theme }) => theme.gridUnit * 3}px
    ${({ theme }) => theme.gridUnit * 4}px;
  border-bottom: ${({ theme }) => getEditorPanelBorder(theme)};
  background: ${({ theme }) => theme.colors.grayscale.light5};
`;

export const EditorPaneTitleBlock = styled.div`
  min-width: 0;
`;

export const EditorPaneTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.m + theme.gridUnit / 4}px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.secondary.dark3};
  letter-spacing: 0.2px;
`;

export const EditorPaneDescription = styled.div`
  margin-top: ${({ theme }) => theme.gridUnit}px;
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.secondary.light1};
  line-height: 1.5;
`;

export const EditorEmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => `${theme.gridUnit * 10}px ${theme.gridUnit * 5}px`};
  color: ${({ theme }) => theme.colors.secondary.light1};
`;

export const EditorEmptyStateMessage = styled.div`
  margin-bottom: ${({ theme }) => theme.gridUnit * 3}px;
`;

export const EditorScrollableBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.grayscale.light5};
`;

export const EditorContent = styled.div`
  padding: ${({ theme }) => theme.gridUnit * 4}px;
`;

export const EditorHero = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.gridUnit * 3}px;
  padding: ${({ theme }) =>
    `${theme.gridUnit * 2.5}px ${theme.gridUnit * 3}px`};
  margin-bottom: ${({ theme }) => theme.gridUnit * 3}px;
  border: ${({ theme }) => getEditorPanelBorder(theme)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.grayscale.light5};
`;

export const EditorHeroMain = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.gridUnit * 3}px;
  min-width: 0;
`;

export const EditorHeroIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
`;

export const EditorHeroTitleWrap = styled.div`
  min-width: 0;
`;

export const EditorHeroTitle = styled.div`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.secondary.dark3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EditorHeroDescription = styled.div`
  margin-top: ${({ theme }) => theme.gridUnit}px;
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.secondary.light1};
  line-height: 1.5;
`;

export const EditorHeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.gridUnit * 2}px;
  flex-wrap: wrap;
`;

export const EditorMetaPill = styled.span<{
  $variant?: 'primary' | 'success' | 'default';
}>`
  padding: ${({ theme }) => `${theme.gridUnit}px ${theme.gridUnit * 2.25}px`};
  border-radius: 999px;
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme, $variant }) => {
    if ($variant === 'primary') {
      return theme.colors.primary.base;
    }
    if ($variant === 'success') {
      return theme.colors.success.base;
    }
    return theme.colors.secondary.base;
  }};
  background: ${({ theme, $variant }) => {
    if ($variant === 'primary') {
      return theme.colors.primary.light4;
    }
    if ($variant === 'success') {
      return theme.colors.success.light1;
    }
    return theme.colors.secondary.light5;
  }};
`;

export const EditorSectionCard = styled.div`
  padding: ${({ theme }) => theme.gridUnit * 3}px;
  margin-bottom: ${({ theme }) => theme.gridUnit * 3}px;
  border: ${({ theme }) => getEditorPanelBorder(theme)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.grayscale.light5};
`;

export const EditorSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.gridUnit * 3}px;
  margin-bottom: ${({ theme }) => theme.gridUnit * 3}px;
`;

export const EditorSectionTitleWrap = styled.div`
  min-width: 0;
`;

export const EditorSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.m}px;
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.secondary.dark3};
`;

export const EditorSectionDescription = styled.div`
  margin-top: ${({ theme }) => theme.gridUnit}px;
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.secondary.light1};
  line-height: 1.5;
`;

export const EditorActionHint = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  color: ${({ theme }) => theme.colors.secondary.light1};
  line-height: 1.5;
`;

export const EditorStickyActions = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.gridUnit * 3}px;
  padding: ${({ theme }) =>
    `${theme.gridUnit * 3}px ${theme.gridUnit * 4}px ${theme.gridUnit * 4}px`};
  border-top: ${({ theme }) => getEditorPanelBorder(theme)};
  background: ${({ theme }) => theme.colors.grayscale.light5};
`;
